import { useLocation, Navigate } from 'react-router-dom';
import ClusterPage, { H2, P, Stat } from '../../../components/cluster/ClusterPage';
import { getVertical, VERTICALS, CLUSTER_CITIES, FACTS } from '../../../data/cluster';

export default function VerticalSpokePage() {
  const { pathname } = useLocation();
  // path: /chatgpt-ads-for-<slug>-florida
  const m = pathname.match(/^\/chatgpt-ads-for-(.+)-florida\/?$/);
  const slug = m ? m[1] : undefined;
  const v = slug ? getVertical(slug) : undefined;
  if (!v) return <Navigate to="/chatgpt-ads-south-florida-home-services" replace />;

  const kw = `chatgpt ads for florida ${v.name.toLowerCase()}`;
  const siblings = VERTICALS.filter((x) => x.slug !== v.slug);

  // Map cluster vertical slug -> rendered explainer video slug (Miami flagship).
  const VIDEO_SLUG: Record<string, string> = {
    roofers: 'roofers',
    'hvac-contractors': 'hvac-companies',
    plumbers: 'plumbers',
    electricians: 'electricians',
    'garage-door-companies': 'garage-door-pros',
    'restoration-companies': 'restoration-pros',
  };
  const videoSlug = VIDEO_SLUG[v.slug];

  return (
    <ClusterPage
      key={v.slug}
      slug={`chatgpt-ads-for-${v.slug}-florida`}
      keyword={kw}
      videoSrc={videoSlug ? `/videos/fame-chatgpt-ads-miami-${videoSlug}.mp4` : undefined}
      videoPoster={videoSlug ? `/videos/fame-chatgpt-ads-miami-${videoSlug}.jpg` : undefined}
      videoName={`ChatGPT Ads for ${v.name} in Miami — FAME explainer`}
      title={`ChatGPT Ads for Florida ${v.name}`}
      h1={`ChatGPT Ads for Florida ${v.name}`}
      eyebrow={`${v.trade} · Miami · Fort Lauderdale · West Palm Beach`}
      leadAnswer={`ChatGPT Ads for Florida ${v.name.toLowerCase()} put a sponsored card beneath ChatGPT answers when a ${v.hint.replace(/"/g, '')}. FAME targets your best ZIP codes at a $3–5 cost-per-click, writes context hints around ${v.job}, and tracks every call and form so you win ${v.trade} jobs from AI search before competitors arrive.`}
      metaDescription={`ChatGPT Ads for Florida ${v.name.toLowerCase()}: ZIP-targeted sponsored placements in ChatGPT for ${v.trade} businesses in Miami, Fort Lauderdale and West Palm Beach. Done-for-you setup, targeting and tracking by FAME.`}
      keywords={`${kw}, chatgpt ads ${v.trade} florida, ai lead generation for ${v.name.toLowerCase()}, ai marketing for florida ${v.name.toLowerCase()}`}
      areaServed={['Miami', 'Fort Lauderdale', 'West Palm Beach', 'Florida']}
      serviceName={`ChatGPT Ads Management for ${v.name}`}
      serviceDescription={`Done-for-you ChatGPT Ads for Florida ${v.trade} contractors: ZIP-targeted campaigns, context-hint copywriting around ${v.job}, conversion tracking and GEO.`}
      faqs={[
        { q: `Can ${v.trade} contractors run ChatGPT Ads in Florida?`, a: `Yes. "Local services" is an explicitly eligible ChatGPT Ads category, which covers ${v.trade}. ${FACTS.selfServe} ${FACTS.noMinimum}` },
        { q: `How are ${v.name.toLowerCase()} campaigns targeted?`, a: `${FACTS.geo} We target your highest-converting ZIPs and write context hints like ${v.hint} so your card appears at the decision moment.` },
        { q: `What does it cost to advertise a ${v.trade} business on ChatGPT?`, a: `${FACTS.cpc} With ${v.job} jobs typically worth $${v.dealLow.toLocaleString()}–$${v.dealHigh.toLocaleString()}, a single booked job pays for weeks of clicks.` },
        { q: `Can you also get my ${v.trade} business recommended inside the answer?`, a: `Yes — that is GEO. We add schema, reviews and citations so ChatGPT names your business organically, while the paid card runs below. Together you own the answer.` },
        { q: `How does FAME bill ${v.name.toLowerCase()}?`, a: `A monthly management retainer covering campaign optimization, context-hint iteration, reporting and conversion tracking. Month-to-month, ad spend separate.` },
      ]}
      breadcrumb={[
        { name: 'Home', url: 'https://floridaaimarketingexperts.com/' },
        { name: 'ChatGPT Ads for South Florida Home Services', url: 'https://floridaaimarketingexperts.com/chatgpt-ads-south-florida-home-services' },
        { name: `ChatGPT Ads for Florida ${v.name}`, url: `https://floridaaimarketingexperts.com/chatgpt-ads-for-${v.slug}-florida` },
      ]}
      upLinks={[
        { label: 'ChatGPT Ads for South Florida Home Services (hub)', to: '/chatgpt-ads-south-florida-home-services' },
        { label: 'How much do ChatGPT Ads cost?', to: '/how-much-do-chatgpt-ads-cost' },
        { label: 'AI marketing for Florida contractors', to: '/ai-marketing-florida-contractors' },
      ]}
      crossLinks={[
        ...CLUSTER_CITIES.map((c) => ({ label: `ChatGPT Ads in ${c.name}`, to: `/chatgpt-ads-${c.slug}` })),
        { label: 'AI receptionist for contractors', to: '/ai-receptionist-florida-contractors' },
        ...siblings.slice(0, 2).map((s) => ({ label: `ChatGPT Ads for ${s.name}`, to: `/chatgpt-ads-for-${s.slug}-florida` })),
      ]}
    >
      <H2>Where your {v.trade} customers actually are</H2>
      <P>
        Florida homeowners increasingly open ChatGPT before Google when something breaks. They describe the
        problem in plain language — a {v.hint.replace(/"/g, '')} — and ChatGPT answers. A sponsored card
        below that answer puts your {v.trade} business in front of a buyer at the exact decision moment.
      </P>
      <Stat>{FACTS.audience}</Stat>

      <H2>Context hints, not keywords</H2>
      <P>
        We do not bid on match-type keywords. We write context hints describing the conversations where a
        {` ${v.trade}`} business should appear, then point each ad group at your best ZIP codes.
      </P>
      <Stat>{FACTS.hints}</Stat>
      <Stat>{FACTS.geo}</Stat>

      <H2>The math for {v.name.toLowerCase()}</H2>
      <P>
        With {v.job} jobs commonly worth ${v.dealLow.toLocaleString()}–${v.dealHigh.toLocaleString()}, the
        channel pays for itself fast. We run the Clicks objective so you only pay when a homeowner clicks
        through to your landing page.
      </P>
      <Stat>{FACTS.cpc}</Stat>

      <H2>Tracking we set up day one</H2>
      <P>
        We instrument phone-click, form-submit and chat-open events via Google Tag Manager and UTM-tag every
        ad URL. {FACTS.attribution} We set that expectation up front — directional learning first, scale once
        the numbers hold.
      </P>

      <H2>Pair it with GEO to own the whole answer</H2>
      <P>
        Paid placement is half. The other half is getting ChatGPT to name your {v.trade} business inside the
        answer itself through schema, reviews and citations.
      </P>
      <Stat>{FACTS.geoLift} {FACTS.conversionRate}</Stat>
    </ClusterPage>
  );
}
